import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, Building2, Upload, ImageOff, ShieldCheck, Download, Trash2, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import AddressFields, { addressPartsFrom } from "../components/AddressFields";
import ProfileEmailsField, { emailsFrom, senderIndexFrom } from "../components/ProfileEmailsField";

const withFormFields = (d) => ({ ...d, ...addressPartsFrom(d), emails: emailsFrom(d), sender_idx: senderIndexFrom(d) });
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export default function Settings() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");
  const [delPassword, setDelPassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  const changePassword = async () => {
    if (pwd.next.length < 8) return toast.error("La nuova password deve avere almeno 8 caratteri");
    if (pwd.next !== pwd.confirm) return toast.error("Le nuove password non coincidono");
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Password aggiornata con successo");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aggiornamento non riuscito");
    }
    setPwdSaving(false);
  };

  const downloadBlob = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportData = async (fmt) => {
    setExporting(true);
    try {
      const { data } = await api.get("/gdpr/export");
      if (fmt === "json") {
        downloadBlob(JSON.stringify(data, null, 2), "myquoteflow-dati.json", "application/json");
      } else {
        const quotes = data.quotes || [];
        const headers = ["quote_number", "title", "status", "client_name", "client_vat", "due_date", "netto", "created_at"];
        const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const rows = quotes.map((q) => [
          q.quote_number, q.title, q.status, q.client?.name, q.client?.vat,
          q.due_date, (q.accepted_totals?.netto ?? q.totals?.netto ?? ""), q.created_at,
        ].map(esc).join(","));
        downloadBlob([headers.join(","), ...rows].join("\n"), "myquoteflow-preventivi.csv", "text/csv");
      }
      toast.success("Esportazione completata");
    } catch (e) {
      toast.error("Errore nell'esportazione dei dati");
    }
    setExporting(false);
  };

  const deleteAccount = async () => {
    if (delConfirm.trim().toUpperCase() !== "ELIMINA") return toast.error('Digita "ELIMINA" per confermare');
    if (!delPassword) return toast.error("Inserisci la tua password");
    setDeleting(true);
    try {
      const { data } = await api.delete("/account", { data: { password: delPassword } });
      toast.success(data.subscription?.had_subscription
        ? "Abbonamento disdetto e account eliminato definitivamente. Ti abbiamo inviato un'email di conferma."
        : "Account e dati eliminati definitivamente. Ti abbiamo inviato un'email di conferma.");
      await logout();
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eliminazione non riuscita");
      setDeleting(false);
    }
  };

  useEffect(() => {
    api.get("/profile").then((r) => setP(withFormFields(r.data)));
  }, []);

  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const logoSrc =
    p && p.logo_path
      ? `${process.env.REACT_APP_BACKEND_URL}/api/public/logo/${p.user_id}?t=${p.logo_ver || ""}`
      : null;

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/logo", fd);
      setP({ ...data, logo_ver: Date.now() });
      toast.success("Logo caricato!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento del logo");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeLogo = async () => {
    const { data } = await api.delete("/profile/logo");
    setP({ ...data, logo_ver: Date.now() });
    toast.success("Logo rimosso");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", {
        business_name: p.business_name || "",
        owner_name: p.owner_name || "",
        vat_number: p.vat_number || "",
        ...addressPartsFrom(p),
        emails: p.emails.map((e) => e.trim()),
        email: (p.emails[p.sender_idx] || "").trim() || p.emails.find((e) => e.trim())?.trim() || "",
        phone: p.phone || "",
        business_type: p.business_type || "libero_professionista",
        tax_regime: p.tax_regime || "forfettario",
        previdenza: p.previdenza || "nessuno",
        cassa_rate: Number(p.cassa_rate) || 4,
        ritenuta_acconto: !!p.ritenuta_acconto,
        addebito_bollo: !!p.addebito_bollo,
      });
      setP(withFormFields(data));
      toast.success("Profilo salvato con successo!");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore nel salvataggio");
    }
    setSaving(false);
  };

  if (!p)
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
          <Building2 className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Profilo Emittente</h1>
          <p className="text-sm text-slate-500">Questi dati appaiono nei preventivi.</p>
        </div>
      </div>

      <div className="space-y-8">
        <div className="bg-card border border-slate-200/80 rounded-xl p-6 shadow-sm space-y-5">
          <h2 className="text-base font-semibold text-slate-900">Dati Anagrafici</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Nome e Cognome">
              <Input data-testid="profile-owner-name" value={p.owner_name || ""} onChange={(e) => set("owner_name", e.target.value)} />
            </Field>
            <Field label="Ragione Sociale / Studio">
              <Input data-testid="profile-business-name" value={p.business_name || ""} onChange={(e) => set("business_name", e.target.value)} />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="P.IVA / Codice Fiscale">
              <Input data-testid="profile-vat" value={p.vat_number || ""} onChange={(e) => set("vat_number", e.target.value)} />
            </Field>
            <Field label="Telefono">
              <Input data-testid="profile-phone" value={p.phone || ""} onChange={(e) => set("phone", e.target.value)} />
            </Field>
          </div>
          <AddressFields value={p} onChange={set} testidPrefix="profile" />
          <ProfileEmailsField
            emails={p.emails}
            sender={p.sender_idx}
            onSender={(i) => set("sender_idx", i)}
            onChange={(i, v) => setP((s) => {
              const emails = s.emails.map((e, j) => (j === i ? v : e));
              const sender_idx = !emails[s.sender_idx]?.trim() ? Math.max(0, emails.findIndex((e) => e.trim())) : s.sender_idx;
              return { ...s, emails, sender_idx };
            })}
          />
        </div>

        <div className="bg-card border border-slate-200/80 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900 mb-1">Logo Studio</h2>
          <p className="text-sm text-slate-500 mb-5">Appare nell'intestazione del preventivo del cliente e nel PDF. PNG, JPG, WEBP o SVG (max 3 MB).</p>
          <div className="flex items-center gap-5">
            <div className="w-24 h-24 rounded-xl border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
              {logoSrc ? (
                <img data-testid="profile-logo-preview" src={logoSrc} alt="Logo" className="w-full h-full object-contain p-2" />
              ) : (
                <ImageOff className="w-7 h-7 text-slate-300" />
              )}
            </div>
            <div className="space-y-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={uploadLogo} className="hidden" data-testid="profile-logo-input" />
              <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="profile-logo-upload-button">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload style={{ width: 15, height: 15 }} className="mr-2" />}
                {logoSrc ? "Cambia logo" : "Carica logo"}
              </Button>
              {logoSrc && (
                <button type="button" onClick={removeLogo} data-testid="profile-logo-remove-button" className="block text-sm text-red-600 hover:underline">
                  Rimuovi logo
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-card border border-slate-200/80 rounded-xl p-6 shadow-sm space-y-5">
          <h2 className="text-base font-semibold text-slate-900">Tipo di Attività e Regime Fiscale</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Tipo di Attività">
              <Select value={p.business_type || "libero_professionista"} onValueChange={(v) => set("business_type", v)}>
                <SelectTrigger data-testid="profile-business-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="libero_professionista">Libero Professionista o Freelance</SelectItem>
                  <SelectItem value="ditta_individuale">Ditta Individuale Artigiano o Commerciante</SelectItem>
                  <SelectItem value="societa">Società di Capitali o Persone</SelectItem>
                  <SelectItem value="senza_piva">Soggetto senza Partita IVA</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Regime Fiscale">
              <Select value={p.tax_regime || "forfettario"} onValueChange={(v) => set("tax_regime", v)}>
                <SelectTrigger data-testid="profile-tax-regime"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="forfettario">Regime Forfettario</SelectItem>
                  <SelectItem value="semplificato">Regime Semplificato</SelectItem>
                  <SelectItem value="ordinario">Regime Ordinario</SelectItem>
                  <SelectItem value="occasionale">Prestazione Occasionale</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Contributo Previdenziale">
            <Select value={p.previdenza || "nessuno"} onValueChange={(v) => set("previdenza", v)}>
              <SelectTrigger data-testid="profile-previdenza" className="sm:max-w-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nessuno">Nessuno</SelectItem>
                <SelectItem value="inps">Rivalsa INPS 4%</SelectItem>
                <SelectItem value="cassa">Cassa Professionale</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {p.previdenza === "cassa" && (
            <Field label="Contributo integrativo della tua cassa">
              <Select value={String(p.cassa_rate || 4)} onValueChange={(v) => set("cassa_rate", Number(v))}>
                <SelectTrigger data-testid="profile-cassa-rate" className="sm:max-w-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2%</SelectItem>
                  <SelectItem value="4">4%</SelectItem>
                  <SelectItem value="5">5%</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1.5" data-testid="profile-cassa-rate-note">Es. geometri (CIPAG) 5%, medici (ENPAM) 2%, la maggior parte delle casse 4%</p>
            </Field>
          )}
          {(p.tax_regime === "ordinario" || p.tax_regime === "semplificato") && (
            <div className="flex items-center justify-between rounded-lg border border-border p-4">
              <div className="pr-4">
                <p className="text-sm font-medium text-slate-900">Soggetto a Ritenuta d'Acconto (20%)</p>
                <p className="text-xs text-slate-500">Applicata solo ai clienti sostituti d'imposta (scelta nel preventivo).</p>
              </div>
              <Switch data-testid="profile-ritenuta" checked={!!p.ritenuta_acconto} onCheckedChange={(v) => set("ritenuta_acconto", v)} />
            </div>
          )}
          {p.tax_regime === "occasionale" && (
            <p className="text-xs text-amber-600 rounded-lg border border-amber-200 bg-amber-50 p-3">Prestazione Occasionale: nessuna IVA e ritenuta d'acconto 20% applicata automaticamente per clienti con P.IVA.</p>
          )}
          <div className="flex items-center justify-between rounded-lg border border-border p-4">
            <div className="pr-4">
              <p className="text-sm font-medium text-slate-900">Addebito Marca da Bollo (2,00 €)</p>
              <p className="text-xs text-slate-500">Addebita al cliente il bollo da 2 € quando è dovuto (importi senza IVA oltre 77,47 €)</p>
            </div>
            <Switch data-testid="profile-bollo" checked={!!p.addebito_bollo} onCheckedChange={(v) => set("addebito_bollo", v)} />
          </div>
        </div>

        <div className="bg-card border border-slate-200/80 rounded-xl p-6 shadow-sm space-y-5" data-testid="security-section">
          <div className="flex items-center gap-2">
            <ShieldCheck style={{ width: 18, height: 18 }} className="text-primary" />
            <h2 className="text-base font-semibold text-slate-900">Sicurezza &middot; Cambia Password</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            <Field label="Password attuale">
              <Input data-testid="pwd-current" type="password" value={pwd.current} onChange={(e) => setPwd((s) => ({ ...s, current: e.target.value }))} />
            </Field>
            <Field label="Nuova password">
              <Input data-testid="pwd-new" type="password" value={pwd.next} onChange={(e) => setPwd((s) => ({ ...s, next: e.target.value }))} />
            </Field>
            <Field label="Conferma nuova">
              <Input data-testid="pwd-confirm" type="password" value={pwd.confirm} onChange={(e) => setPwd((s) => ({ ...s, confirm: e.target.value }))} />
            </Field>
          </div>
          <Button data-testid="pwd-save" onClick={changePassword} disabled={pwdSaving || !pwd.current || !pwd.next} variant="outline" className="font-semibold">
            {pwdSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Aggiorna Password
          </Button>
        </div>

        <div className="bg-card border border-slate-200/80 rounded-xl p-6 shadow-sm space-y-5" data-testid="gdpr-section">
          <div className="flex items-center gap-2">
            <ShieldCheck style={{ width: 18, height: 18 }} className="text-primary" />
            <h2 className="text-base font-semibold text-slate-900">Privacy &amp; Conformità GDPR</h2>
          </div>
          <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4 flex-wrap">
            <div className="pr-4 min-w-[200px]">
              <p className="text-sm font-medium text-slate-900">Esporta i tuoi dati (Art. 20 — Portabilità)</p>
              <p className="text-xs text-slate-500">Scarica tutti i tuoi dati e preventivi in formato JSON o CSV.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" data-testid="gdpr-export-json" onClick={() => exportData("json")} disabled={exporting}>
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download style={{ width: 15, height: 15 }} className="mr-2" /> JSON</>}
              </Button>
              <Button variant="outline" data-testid="gdpr-export-csv" onClick={() => exportData("csv")} disabled={exporting}>
                <Download style={{ width: 15, height: 15 }} className="mr-2" /> CSV
              </Button>
            </div>
          </div>
          <div className="flex items-start justify-between gap-4 rounded-lg border border-red-200 bg-red-50/50 p-4 flex-wrap">
            <div className="pr-4 min-w-[200px]">
              <p className="text-sm font-medium text-red-900">Elimina account (Art. 17 — Diritto all'oblio)</p>
              <p className="text-xs text-red-700/80">Cancella definitivamente l'account e TUTTI i dati associati. Operazione irreversibile.</p>
              <p className="text-xs text-slate-600 mt-1.5" data-testid="settings-renewal-hint">Vuoi solo smettere di pagare? <a href="/billing" className="font-semibold text-primary underline">Disdici il rinnovo</a>: mantieni Pro fino alla scadenza e i tuoi dati restano al sicuro.</p>
            </div>
            <Button variant="outline" data-testid="gdpr-delete-open" onClick={() => { setDelConfirm(""); setDelPassword(""); setDelOpen(true); }} className="border-red-300 text-red-700 hover:bg-red-100">
              <Trash2 style={{ width: 15, height: 15 }} className="mr-2" /> Elimina account
            </Button>
          </div>
        </div>

        <Button data-testid="profile-save-button" onClick={save} disabled={saving} className="h-11 font-semibold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save style={{ width: 16, height: 16 }} className="mr-2" />}
          Salva Profilo
        </Button>
      </div>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="max-w-md" data-testid="gdpr-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle style={{ width: 20, height: 20 }} /> Elimina definitivamente l'account
            </DialogTitle>
            <DialogDescription>
              Questa azione è <strong>irreversibile</strong>. Verranno cancellati per sempre il tuo account, tutti i preventivi, il listino, i feedback e ogni dato associato.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Digita ELIMINA per confermare</Label>
              <Input data-testid="gdpr-delete-confirm-input" value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} placeholder="ELIMINA" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Conferma la tua password</Label>
              <Input data-testid="gdpr-delete-password-input" type="password" value={delPassword} onChange={(e) => setDelPassword(e.target.value)} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)}>Annulla</Button>
            <Button data-testid="gdpr-delete-confirm-button" onClick={deleteAccount} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Elimina per sempre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
